import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Row, Col, Input, Empty, Modal, Tag, Button } from 'antd';
import {
  SearchOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  TrophyOutlined,
  CompassOutlined,
  BookOutlined,
  DollarCircleOutlined,
  DashboardOutlined,
  RightOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { learningApi } from '../../../api/learning.api.js';
import { courseApi } from '../../../api/course.api.js';
import { learningPathApi } from '../../../api/learning-path.api.js';
import { planApi } from '../../../api/plan.api.js';
import { CourseCard } from '../components/CourseCard.jsx';
import { CategoryFilter } from '../components/CategoryFilter.jsx';
import { LearningPathCard } from '../components/LearningPathCard.jsx';
import { PricingPlans } from '../components/PricingPlans.jsx';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner.jsx';
import { ErrorState } from '../../../components/common/ErrorState.jsx';
import '../styles/course-list.css';

const TOPIC_ORDER = {
  'sale-ban-hang-tu-van': 1,
  'marketing-nha-thuoc': 2,
  'cong-nghe-ai': 3,
  'van-hanh-nha-thuoc': 4,
  'nhan-su-nha-thuoc': 5,
  'chuyen-mon-duoc': 6,
  'phap-ly-thu-tuc': 7,
};

export function CourseListPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'topics';
  const initialCategoryId = searchParams.get('categoryId') || null;

  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [learningPaths, setLearningPaths] = useState([]);
  const [plans, setPlans] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(initialCategoryId);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sync state with URL search params if changed externally
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
    const catFromUrl = searchParams.get('categoryId');
    setSelectedCategory(catFromUrl || null);
  }, [searchParams]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', key);
    setSearchParams(newParams);
  };

  const handleSelectCategory = (catId) => {
    setSelectedCategory(catId);
    const newParams = new URLSearchParams(searchParams);
    if (catId) {
      newParams.set('categoryId', catId);
    } else {
      newParams.delete('categoryId');
    }
    setSearchParams(newParams);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [catsRes, coursesRes, pathsRes, plansRes] = await Promise.all([
        courseApi.getCategories(),
        courseApi.getCourses({
          categoryId: selectedCategory || undefined,
          search: searchKeyword || undefined,
        }),
        learningPathApi.getAllPaths(),
        planApi.getAllPlans(),
      ]);
      setCategories(catsRes.data || []);
      const rawCourses = coursesRes.data || [];
      const sortedCourses = [...rawCourses].sort((a, b) => {
        const orderA = TOPIC_ORDER[a.slug] ?? 99;
        const orderB = TOPIC_ORDER[b.slug] ?? 99;
        return orderA - orderB;
      });
      setCourses(sortedCourses);
      setLearningPaths(pathsRes.data || []);
      setPlans(plansRes.data || []);
    } catch (err) {
      setError(err.message || 'Không thể tải dữ liệu đào tạo');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchKeyword]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchMyProgress = useCallback(async () => {
    if (!isAuthenticated) {
      setMyCourses([]);
      return;
    }
    try {
      const res = await learningApi.getMyCourses();
      setMyCourses(res.data || []);
    } catch (err) {
      console.warn('Không thể tải tiến độ học tập:', err);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchMyProgress();
  }, [fetchMyProgress]);

  // Tính tổng số module bài học thực tế (32 module theo chương trình)
  const totalModules = courses.reduce((sum, c) => sum + (c.totalLessons || 0), 0) || 32;

  // Map progress từ myCourses theo courseId
  const courseProgressMap = new Map();
  myCourses.forEach((item) => {
    if (item.course?.id) {
      courseProgressMap.set(item.course.id, item.progress);
    }
  });

  // Tổng số module đã hoàn thành
  const completedModules = myCourses.reduce((sum, item) => {
    const ids = item.progress?.completedLessonIds;
    return sum + (Array.isArray(ids) ? ids.length : 0);
  }, 0);

  const overallPercentage = totalModules > 0 ? Math.min(100, Math.round((completedModules / totalModules) * 100)) : 0;
  const completedCoursesCount = myCourses.filter((item) => item.progress?.isCompleted).length;

  const tabItems = [
    {
      key: 'topics',
      label: (
        <span style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
          <BookOutlined /> 7 Chuyên Đề Đào Tạo ({courses.length})
        </span>
      ),
      children: (
        <div style={{ marginTop: 12 }}>
          {/* Filter and Search Bar */}
          <div
            className="course-filter-row"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              marginBottom: 20,
            }}
          >
            <CategoryFilter
              categories={categories}
              selectedCategoryId={selectedCategory}
              onSelectCategory={handleSelectCategory}
            />

            <div className="course-filter-search" style={{ width: 280, marginBottom: 20 }}>
              <Input
                placeholder="Tìm kiếm chuyên đề..."
                prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                allowClear
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
            </div>
          </div>

          {courses.length === 0 ? (
            <Empty
              description="Không tìm thấy chuyên đề phù hợp với tiêu chí lọc của bạn"
              style={{ margin: '60px 0' }}
            />
          ) : (
            <Row gutter={[24, 24]}>
              {courses.map((course) => (
                <Col xs={24} sm={12} lg={8} key={course.id}>
                  <CourseCard course={course} />
                </Col>
              ))}
            </Row>
          )}
        </div>
      ),
    },
    {
      key: 'paths',
      label: (
        <span style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
          <CompassOutlined /> 3 Lộ Trình Đóng Gói
        </span>
      ),
      children: (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 14, color: '#475569', margin: 0 }}>
              Chương trình đào tạo đóng gói theo đúng <strong>3 chân dung học viên</strong>: Người sắp mở mới, Chủ nhà thuốc muốn tăng trưởng điểm bán, và Chủ muốn nhân bản chuỗi.
            </p>
          </div>

          <Row gutter={[24, 24]}>
            {learningPaths.map((path) => (
              <Col xs={24} md={12} lg={8} key={path.id || path.code}>
                <LearningPathCard path={path} />
              </Col>
            ))}
          </Row>
        </div>
      ),
    },
    {
      key: 'pricing',
      label: (
        <span style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
          <DollarCircleOutlined /> Gói Học & Bảng Giá
        </span>
      ),
      children: <PricingPlans plans={plans} />,
    },
  ];

  return (
    <div className="main-container">
      {/* Banner giới thiệu */}
      <div
        className="course-hero"
        style={{
          background: 'linear-gradient(135deg, #004724 0%, #005b2f 55%, #006b37 100%)',
          color: '#ffffff',
          borderRadius: 18,
          padding: '36px 36px',
          marginBottom: 28,
          boxShadow: '0 14px 36px rgba(0, 91, 47, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Row gutter={[36, 28]} align="middle" style={{ position: 'relative', zIndex: 1 }}>
          {/* Cột thông tin bên trái */}
          <Col xs={24} lg={15}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 14px',
                    borderRadius: 20,
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    color: '#ffffff',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.3px',
                  }}
                >
                  ✦ HỆ THỐNG ĐÀO TẠO THỰC CHIẾN 2026
                </span>

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 12px',
                    borderRadius: 20,
                    background: 'rgba(0, 0, 0, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    color: '#e2f7ec',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  Thế Thảo Pharma LMS
                </span>
              </div>

              <h1
                className="course-hero-title"
                style={{
                  fontSize: 27,
                  fontWeight: 800,
                  color: '#ffffff',
                  marginBottom: 14,
                  letterSpacing: '-0.4px',
                  lineHeight: 1.35,
                }}
              >
                LMS Đào Tạo Quản Trị & Kinh Doanh Nhà Thuốc
              </h1>

              <p className="course-hero-desc" style={{ fontSize: 14.5, color: '#e2f7ec', lineHeight: 1.65, marginBottom: 24, maxWidth: 640 }}>
                Chương trình thực chiến <strong>32 Module</strong> thuộc <strong>7 Chuyên đề</strong> độc quyền từ{' '}
                <strong style={{ color: '#ffffff' }}>Thế Thảo Pharma</strong> – đồng hành cùng nhà thuốc vươn xa. Trang bị toàn diện từ tư vấn quầy, marketing bán lẻ, P&L tài chính đến tự động hóa AI và pháp lý GPP.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    color: '#ffffff',
                    background: 'rgba(0, 0, 0, 0.18)',
                    padding: '8px 14px',
                    borderRadius: 10,
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    fontWeight: 500,
                  }}
                >
                  <SafetyCertificateOutlined style={{ color: '#4ade80', fontSize: 16 }} />
                  <span>32 Module bài học chuyên sâu</span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    color: '#ffffff',
                    background: 'rgba(0, 0, 0, 0.18)',
                    padding: '8px 14px',
                    borderRadius: 10,
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    fontWeight: 500,
                  }}
                >
                  <TeamOutlined style={{ color: '#86efac', fontSize: 16 }} />
                  <span>88 File công cụ Excel/Word/PDF</span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    color: '#ffffff',
                    background: 'rgba(0, 0, 0, 0.18)',
                    padding: '8px 14px',
                    borderRadius: 10,
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    fontWeight: 500,
                  }}
                >
                  <TrophyOutlined style={{ color: '#fde047', fontSize: 16 }} />
                  <span>3 Lộ trình học đóng gói</span>
                </div>
              </div>

              {/* Thanh ngang Tiến độ của tôi */}
              <div
                onClick={() => {
                  if (isAuthenticated) {
                    setIsProgressModalOpen(true);
                  } else {
                    navigate('/login');
                  }
                }}
                style={{
                  marginTop: 18,
                  padding: '12px 18px',
                  background: 'rgba(0, 0, 0, 0.26)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 12,
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  maxWidth: 640,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
                  e.currentTarget.style.borderColor = 'rgba(74, 222, 128, 0.6)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 18px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.26)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Dòng thông tin: Tiêu đề + X/32 Module + % hoàn thành */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DashboardOutlined style={{ color: '#4ade80', fontSize: 16 }} />
                    <span style={{ color: '#ffffff', fontWeight: 700, fontSize: 13.5 }}>
                      Tiến độ của tôi:
                    </span>
                    <span
                      style={{
                        background: 'rgba(74, 222, 128, 0.2)',
                        color: '#4ade80',
                        border: '1px solid rgba(74, 222, 128, 0.35)',
                        borderRadius: 14,
                        padding: '1px 10px',
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '0.2px',
                      }}
                    >
                      {isAuthenticated ? `${completedModules}/${totalModules} Module` : `0/${totalModules} Module`}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#fde047', fontWeight: 800, fontSize: 15 }}>
                      {isAuthenticated ? `${overallPercentage}%` : '0%'}
                    </span>
                    <span
                      style={{
                        color: '#a7f3d0',
                        fontSize: 12,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontWeight: 500,
                      }}
                    >
                      {isAuthenticated ? 'Xem chi tiết' : 'Đăng nhập để xem'}
                      <RightOutlined style={{ fontSize: 10 }} />
                    </span>
                  </div>
                </div>

                {/* Thanh ngang thể hiện % đã học */}
                <div
                  style={{
                    width: '100%',
                    height: 7,
                    borderRadius: 6,
                    background: 'rgba(255, 255, 255, 0.16)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${isAuthenticated ? overallPercentage : 0}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #4ade80 0%, #22c55e 60%, #fde047 100%)',
                      borderRadius: 6,
                      transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: overallPercentage > 0 ? '0 0 8px rgba(74, 222, 128, 0.6)' : 'none',
                    }}
                  />
                </div>
              </div>
            </div>
          </Col>

          {/* Cột hình ảnh nhà thuốc bên phải */}
          <Col xs={24} lg={9}>
            <div
              style={{
                position: 'relative',
                borderRadius: 14,
                overflow: 'hidden',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                aspectRatio: '16/10',
                background: '#004724',
              }}
            >
              <img
                src="/images/pharmacy-banner-2.jpg"
                alt="Không gian nhà thuốc thực chiến chuẩn GPP"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.4s ease',
                }}
                onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.04)')}
                onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '8px 14px',
                  background: 'linear-gradient(to top, rgba(0, 45, 24, 0.9) 0%, rgba(0, 45, 24, 0) 100%)',
                  color: '#ffffff',
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: '#4ade80',
                  }}
                />
                Mô hình Nhà Thuốc Thực Chiến Chuẩn GPP
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {loading ? (
        <LoadingSpinner tip="Đang tải dữ liệu đào tạo..." minHeight="300px" />
      ) : error ? (
        <ErrorState title="Lỗi tải dữ liệu" subTitle={error} onRetry={fetchData} />
      ) : (
        <div style={{ marginBottom: 32 }}>
          <div className="course-tabs-nav">
            {tabItems.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`course-tab-btn${activeTab === tab.key ? ' is-active' : ''}`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {tabItems.find((tab) => tab.key === activeTab)?.children}
        </div>
      )}

      {/* Modal chi tiết các tiến độ khi click vào thanh ngang */}
      <Modal
        open={isProgressModalOpen}
        onCancel={() => setIsProgressModalOpen(false)}
        footer={null}
        width={720}
        centered
        styles={{ body: { padding: '20px 24px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 10,
                background: '#005537',
                color: '#4ade80',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
              }}
            >
              <DashboardOutlined />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                Tiến Độ Học Tập Chi Tiết
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                Học viên: <strong>{user?.fullName || user?.email || 'Học viên'}</strong> • Hệ thống 32 Module Thực Chiến
              </div>
            </div>
          </div>
        </div>

        {/* 3 Thẻ thống kê tổng quan */}
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          <Col xs={24} sm={8}>
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: 4 }}>Tổng Tiến Độ</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#005537' }}>{overallPercentage}%</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>hoàn thành toàn khóa</div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: 4 }}>Module Đã Học</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0284c7' }}>
                {completedModules} <span style={{ fontSize: 14, fontWeight: 500, color: '#64748b' }}>/ {totalModules}</span>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>bài học chuyên sâu</div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: 4 }}>Chuyên Đề Hoàn Thành</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#16a34a' }}>
                {completedCoursesCount} <span style={{ fontSize: 14, fontWeight: 500, color: '#64748b' }}>/ {courses.length}</span>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>chuyên đề đào tạo</div>
            </div>
          </Col>
        </Row>

        {/* Danh sách từng chuyên đề */}
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>
          Tiến độ chi tiết từng chuyên đề ({courses.length} chuyên đề):
        </div>

        <div style={{ maxHeight: '380px', overflowY: 'auto', paddingRight: 4, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {courses.map((course, idx) => {
            const prog = courseProgressMap.get(course.id);
            const compCount = Array.isArray(prog?.completedLessonIds) ? prog.completedLessonIds.length : 0;
            const cTotal = course.totalLessons || 0;
            const cPercent = cTotal > 0 ? Math.min(100, Math.round((compCount / cTotal) * 100)) : 0;
            const isDone = prog?.isCompleted || cPercent >= 100;

            return (
              <div
                key={course.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                  background: isDone ? '#f0fdf4' : compCount > 0 ? '#f0f9ff' : '#ffffff',
                  gap: 14,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        background: isDone ? '#16a34a' : compCount > 0 ? '#0284c7' : '#94a3b8',
                        color: '#ffffff',
                        fontSize: 11,
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: 13.5, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {course.title}
                    </span>
                    {isDone ? (
                      <Tag color="success" style={{ margin: 0, fontSize: 11 }}>Hoàn thành</Tag>
                    ) : compCount > 0 ? (
                      <Tag color="processing" style={{ margin: 0, fontSize: 11 }}>Đang học</Tag>
                    ) : (
                      <Tag style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>Chưa học</Tag>
                    )}
                  </div>

                  {/* Progress bar mini cho chuyên đề */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        flex: 1,
                        height: 6,
                        borderRadius: 4,
                        background: '#e2e8f0',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${cPercent}%`,
                          height: '100%',
                          background: isDone ? '#16a34a' : '#0284c7',
                          borderRadius: 4,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, minWidth: 80, textAlign: 'right' }}>
                      {compCount}/{cTotal} Module ({cPercent}%)
                    </span>
                  </div>
                </div>

                <Button
                  size="small"
                  type={compCount > 0 ? 'primary' : 'default'}
                  icon={<PlayCircleOutlined />}
                  style={{
                    flexShrink: 0,
                    fontWeight: 600,
                    fontSize: 12,
                    background: compCount > 0 ? '#005537' : undefined,
                    borderColor: compCount > 0 ? '#005537' : undefined,
                  }}
                  onClick={() => {
                    setIsProgressModalOpen(false);
                    navigate(`/courses/${course.slug}`);
                  }}
                >
                  {isDone ? 'Ôn lại' : compCount > 0 ? 'Học tiếp' : 'Vào học'}
                </Button>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={() => setIsProgressModalOpen(false)}>Đóng</Button>
          <Button
            type="primary"
            style={{ background: '#005537' }}
            onClick={() => {
              setIsProgressModalOpen(false);
              navigate('/dashboard');
            }}
          >
            Đến Trang Tiến Độ Của Tôi
          </Button>
        </div>
      </Modal>
    </div>
  );
}
